import type {
    RespondentResult,
    CategoryStats,
    Insight,
    TeamStats,
    Employee,
} from '../types/results';
import type { SurveyQuestion, ScaleOption } from '../types';
import { getHebrewName, getEnglishName } from '../constants/muscles';

/**
 * Convert a RespondentResult to the Employee format for visualizations.
 * Expects scores to be pre-calculated by SQL views (respondent_category_scores).
 * The category_stats field should contain normalized 1-10 scores from the database.
 */
export function toEmployee(
    respondent: RespondentResult,
    _questions?: SurveyQuestion[],
    _answerScale?: Record<string, ScaleOption[]>
): Employee | null {
    let scores: any = respondent.category_stats;

    // Safe parse category_stats if stringified
    if (typeof scores === 'string') {
        try {
            scores = JSON.parse(scores);
        } catch {
            scores = null;
        }
    }

    if (!scores || Object.keys(scores).length === 0) {
        return null;
    }

    const normalizedScores: Record<string, number> = Object.fromEntries(
        Object.entries(scores as Record<string, any>).map(([cat, val]) => {
            return [cat, Number(val)];
        })
    );

    return {
        id: respondent.id,
        name: respondent.name,
        email: respondent.email,
        scores: normalizedScores,
        dateOfBirth: respondent.date_of_birth || undefined,
        gender: respondent.gender || undefined,
        ageBucket: respondent.age_bucket || undefined,
        genderNormalized: respondent.gender_normalized || undefined,
    };
}

/**
 * Convert multiple respondents to Employee format.
 * Filters out respondents without valid scores.
 */
export function toEmployees(
    respondents: RespondentResult[],
    questions?: SurveyQuestion[],
    answerScale?: Record<string, ScaleOption[]>
): Employee[] {
    return respondents
        .map(r => toEmployee(r, questions, answerScale))
        .filter((e): e is Employee => e !== null);
}

/**
 * Get all unique categories from a list of respondents or template questions
 */
export function extractCategories(
    respondents: RespondentResult[],
    questions?: SurveyQuestion[]
): string[] {
    const categories = new Set<string>();

    for (const r of respondents) {
        if (r.category_stats) {
            Object.keys(r.category_stats).forEach(cat => categories.add(cat));
        }
    }

    // If no categories found in respondents, try to get them from questions
    if (categories.size === 0 && questions) {
        questions.forEach(q => {
            if (q.category) categories.add(q.category);
        });
    }

    return Array.from(categories).sort();
}

/**
 * Calculate team-level statistics from respondent results
 */
export function getTeamStats(
    respondents: RespondentResult[],
    questions?: SurveyQuestion[],
    answerScale?: Record<string, ScaleOption[]>
): TeamStats {
    const employees = toEmployees(respondents, questions, answerScale);
    const categories = extractCategories(respondents, questions);

    if (employees.length === 0 || categories.length === 0) {
        return {
            averages: {},
            superpower: ['N/A', 0],
            blindspot: ['N/A', 0],
            respondentCount: respondents.length,
            completedCount: 0,
        };
    }

    const averages: CategoryStats = {};

    for (const category of categories) {
        const scores = employees
            .map(e => e.scores[category])
            .filter((s): s is number => typeof s === 'number');

        if (scores.length > 0) {
            averages[category] = scores.reduce((a, b) => a + b, 0) / scores.length;
        }
    }

    const sorted = Object.entries(averages).sort((a, b) => b[1] - a[1]);

    return {
        averages,
        superpower: sorted[0] || ['N/A', 0],
        blindspot: sorted[sorted.length - 1] || ['N/A', 0],
        respondentCount: respondents.length,
        completedCount: employees.length,
    };
}

/**
 * Analyze an individual employee and generate insights
 */
export function analyzeIndividual(
    employee: Employee,
    demographicData?: {
        ageGroupAverages?: CategoryStats | null;
        genderGroupAverages?: CategoryStats | null;
        ageBucket?: string | null;
        genderNormalized?: string | null;
    }
): Insight[] {
    // Normalize scores to English category names (matching PDF report logic)
    // This ensures compatibility whether scores come with Hebrew or English keys
    const normalizedScores: Record<string, number> = {};
    for (const [key, value] of Object.entries(employee.scores)) {
        const englishKey = getEnglishName(key) || key; // If already English, keep as-is
        normalizedScores[englishKey] = value;
    }

    const insights: Insight[] = [];
    const scores = Object.values(normalizedScores);
    const entries = Object.entries(normalizedScores) as [string, number][];

    if (entries.length === 0) {
        return [{
            type: 'info',
            title: 'אין נתונים',
            message: 'עדיין אין מספיק נתונים לניתוח עבור משתתף זה.',
        }];
    }

    entries.sort((a, b) => b[1] - a[1]);
    const topMuscle = entries[0];
    const lowMuscle = entries[entries.length - 1];

    insights.push({
        type: 'success',
        title: `העוגן החזק: ${getHebrewName(topMuscle[0])}`,
        message: `היכולת שלך ב${getHebrewName(topMuscle[0])} (ציון ${topMuscle[1].toFixed(2)}) מהווה נכס משמעותי. זהו המשאב הטבעי שזמין עבורך במצבי לחץ.`,
    });

    const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
    const variance = scores.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / scores.length;
    const stdDev = Math.sqrt(variance);

    if (stdDev > 1.7) {
        insights.push({
            type: 'info',
            title: 'מיקוד אנרגיה נוכחי',
            message: `כרגע ניכרת העדפה טבעית להישען על ${getHebrewName(topMuscle[0])}. כדי להרחיב את גמישות התגובה, כדאי לשלב כוחות נוספים.`,
        });
        insights.push({
            type: 'alert',
            title: `הזדמנות לאיזון: ${getHebrewName(lowMuscle[0])}`,
            message: `חיזוק ${getHebrewName(lowMuscle[0])} (ציון ${lowMuscle[1].toFixed(2)}) יכול לסייע במניעת שחיקה של השרירים החזקים ולאזן את התמונה הכוללת.`,
        });
    } else {
        insights.push({
            type: 'success',
            title: 'איזון מרשים',
            message: 'פיזור האנרגיה בין השרירים השונים מאוזן, מה שמאפשר תנועה חלקה בין סגנונות פעולה שונים בהתאם למצב.',
        });
        if (lowMuscle[1] < 6.5) {
            insights.push({
                type: 'info',
                title: `נקודה למחשבה: ${getHebrewName(lowMuscle[0])}`,
                message: `למרות האיזון הטוב, תשומת לב ל${getHebrewName(lowMuscle[0])} עשויה לדייק עוד יותר את יכולת ההתמודדות.`,
            });
        }
    }

    const initiative = normalizedScores['Initiative'] ?? 0;
    const systematic = normalizedScores['Systematic'] ?? 0;
    if (initiative > 7.7 && systematic < 4.4) {
        insights.push({
            type: 'alert',
            title: 'ניהול אנרגיה: התחלה vs סיום',
            message: 'האנרגיה שלך להתחיל דברים (יוזמה) גבוהה מאוד. מומלץ להיעזר בכלים או שותפים לחיזוק שלבי הסיום והפרטים (שיטתיות) כדי למקסם את האימפקט.',
        });
    }

    const socialFlex = normalizedScores['Social Flexibility'] ?? 0;
    if (socialFlex < 4.4) {
        insights.push({
            type: 'info',
            title: 'הרחבת מעגלי תמיכה',
            message: 'יש לך יכולת עצמאית גבוהה. זכור שחוסן לאורך זמן נשען גם על היכולת להפעיל רשתות תמיכה ולהיעזר באחרים.',
        });
    }

    // Age group comparison (only when data available)
    if (demographicData?.ageGroupAverages) {
        const ageAvg: Record<string, number> = {};
        for (const [key, value] of Object.entries(demographicData.ageGroupAverages)) {
            const englishKey = getEnglishName(key) || key;
            ageAvg[englishKey] = value;
        }
        let bestGap = { cat: '', gap: 0 };
        for (const [cat, score] of Object.entries(normalizedScores)) {
            const groupAvg = ageAvg[cat];
            if (groupAvg !== undefined) {
                const gap = score - groupAvg;
                if (gap > bestGap.gap) bestGap = { cat, gap };
            }
        }
        if (bestGap.gap > 0.5) {
            insights.push({
                type: 'success',
                title: `יתרון בקבוצת גיל ${demographicData.ageBucket}`,
                message: `הציון שלך ב${getHebrewName(bestGap.cat)} (${normalizedScores[bestGap.cat].toFixed(2)}) גבוה מהממוצע בקבוצת הגיל שלך ב-${bestGap.gap.toFixed(2)} נקודות. זהו חוזק יחסי משמעותי.`,
            });
        }
    }

    // Gender group comparison (only when data available)
    if (demographicData?.genderGroupAverages) {
        const genderAvg: Record<string, number> = {};
        for (const [key, value] of Object.entries(demographicData.genderGroupAverages)) {
            const englishKey = getEnglishName(key) || key;
            genderAvg[englishKey] = value;
        }
        const genderLabel = demographicData.genderNormalized === 'female' ? 'נשים' : 'גברים';
        let bestCat = { cat: '', gap: 0 };
        for (const [cat, score] of Object.entries(normalizedScores)) {
            const groupAvg = genderAvg[cat];
            if (groupAvg !== undefined) {
                const gap = score - groupAvg;
                if (gap > bestCat.gap) bestCat = { cat, gap };
            }
        }
        if (bestCat.gap > 0.5) {
            insights.push({
                type: 'info',
                title: `חוזק יחסי בקרב ${genderLabel}`,
                message: `בהשוואה ל${genderLabel} בקבוצה, התחום החזק ביותר שלך הוא ${getHebrewName(bestCat.cat)}. זו נקודת מינוף שכדאי למנף.`,
            });
        }
    }

    return insights;
}

/**
 * Calculate Pearson correlation between two arrays.
 */
export function calculateCorrelation(x: number[], y: number[]): number {
    const n = x.length;
    if (n === 0 || n !== y.length) return 0;

    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((a, b, i) => a + b * y[i], 0);
    const sumX2 = x.reduce((a, b) => a + b * b, 0);
    const sumY2 = y.reduce((a, b) => a + b * b, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    if (denominator === 0) return 0;
    return numerator / denominator;
}

/**
 * Calculate imbalance score for an employee (standard deviation of scores).
 */
export function getImbalanceScore(employee: Employee): number {
    const scores = Object.values(employee.scores);
    if (scores.length === 0) return 0;

    const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
    const variance = scores.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / scores.length;
    return Math.sqrt(variance);
}

/**
 * Culture type mapping based on dominant muscle
 * Based on Competing Values Framework (CVF)
 */
const CULTURE_TYPE_MAP: Record<string, string> = {
    // Execution-focused (ממוקדת ביצוע) - Hierarchy/Control
    'Systematic': 'ממוקדת ביצוע',
    'Focus/Purpose': 'ממוקדת ביצוע',

    // Action-oriented (מוכוונת פעולה) - Adhocracy/Create + Agility
    'Initiative': 'מוכוונת פעולה',
    'Cognitive Flexibility': 'מוכוונת פעולה',

    // People-oriented (מוכוונת אנשים) - Clan/Collaborate
    'Social Flexibility': 'מוכוונת אנשים',
    'Positive Attitude – Self': 'מוכוונת אנשים',
    'Positive Attitude – World': 'מוכוונת אנשים',
};

/**
 * Get culture type based on dominant muscle
 * Returns 'מאוזנת' only for unknown data or ties
 * Handles both Hebrew and English muscle names
 */
export function getCultureType(superpower: string): string {
    // Convert Hebrew to English if needed (superpower may come in either language)
    const englishName = getEnglishName(superpower) || superpower;
    return CULTURE_TYPE_MAP[englishName] || 'מאוזנת';
}

